import React, { useMemo, useState } from 'react';
import { X, CircleAlert } from 'lucide-react';
import { useToast } from './ToastProvider';
import { moratoriosAPI, describirError } from '../services/api';
import { formatoMoneda } from '../utils/finanzas';
import '../styles/FormModal.css';
import '../styles/Pages.css';

// Catálogo inicial (P08-B0). OTRO exige observaciones; el backend lo revalida.
export const MOTIVOS_CONDONACION = [
  { value: 'CAUSA_AJENA_AL_CLIENTE', label: 'Causa ajena al cliente' },
  { value: 'ERROR_OPERATIVO', label: 'Error operativo' },
  { value: 'CONTINGENCIA', label: 'Contingencia' },
  { value: 'AUTORIZACION_COMERCIAL', label: 'Autorización comercial' },
  { value: 'CLIENTE_ESTRATEGICO', label: 'Cliente estratégico' },
  { value: 'OTRO', label: 'Otro' }
];

/**
 * "Condonar moratorio" (P08-B0): decisión comercial autorizada, total o
 * parcial. No borra el cargo: el backend registra una condonación auditada
 * (permiso MORATORIOS_CONDONAR) y recalcula el pendiente bajo bloqueo.
 *
 * @param {object} props.cargo - cargo de moratorio serializado (cuotaId, numeroPago, importeOriginal, importePendiente).
 */
function CondonarMoratorioModal({ creditoId, cargo, onClose, onCondonado }) {
  const toast = useToast();
  const pendiente = Number(cargo.importePendiente);
  const [tipo, setTipo] = useState('TOTAL');
  const [importe, setImporte] = useState(pendiente.toFixed(2));
  const [motivoCodigo, setMotivoCodigo] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const errores = useMemo(() => {
    const lista = [];
    const n = Number(importe);
    if (!/^\d+(\.\d{1,2})?$/.test(importe) || !(n > 0)) lista.push('El importe debe ser mayor a cero (hasta 2 decimales).');
    else if (n > pendiente + 0.000001) lista.push(`El importe no puede exceder el moratorio pendiente (${formatoMoneda(pendiente)}).`);
    if (!motivoCodigo) lista.push('Selecciona un motivo.');
    if (motivoCodigo === 'OTRO' && observaciones.trim().length < 3) lista.push('Para el motivo "Otro" las observaciones son obligatorias.');
    return lista;
  }, [importe, pendiente, motivoCodigo, observaciones]);

  const cambiarTipo = (t) => {
    setTipo(t);
    if (t === 'TOTAL') setImporte(pendiente.toFixed(2));
    else if (Number(importe) >= pendiente) setImporte('');
  };
  const tipoEfectivo = Math.abs(Number(importe) - pendiente) < 0.005 ? 'TOTAL' : 'PARCIAL';

  const ejecutar = async () => {
    setEnviando(true);
    try {
      await moratoriosAPI.condonar(creditoId, {
        cuotaId: cargo.cuotaId,
        importe: Number(importe).toFixed(2),
        tipo: tipoEfectivo,
        motivoCodigo,
        observaciones: observaciones.trim() || undefined
      });
      await onCondonado();
    } catch (e) {
      toast.error(describirError(e).mensaje);
      setConfirmando(false);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="condonar-titulo">
        <div className="modal-header">
          <h2 id="condonar-titulo">Condonar moratorio · cuota #{cargo.numeroPago}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Cerrar">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <form className="modal-form" onSubmit={(e) => { e.preventDefault(); if (!errores.length) setConfirmando(true); }}>
          <dl className="pagos-detalle-lista">
            <div><dt>Moratorio generado</dt><dd>{formatoMoneda(cargo.importeOriginal)}</dd></div>
            <div><dt>Moratorio pendiente</dt><dd>{formatoMoneda(pendiente)}</dd></div>
          </dl>

          {!confirmando ? (
            <>
              <div className="form-group">
                <label htmlFor="condonar-tipo">Tipo</label>
                <select id="condonar-tipo" value={tipo} onChange={(e) => cambiarTipo(e.target.value)}>
                  <option value="TOTAL">Total</option>
                  <option value="PARCIAL">Parcial</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="condonar-importe">Importe a condonar</label>
                <input
                  id="condonar-importe"
                  type="text"
                  inputMode="decimal"
                  value={importe}
                  onChange={(e) => setImporte(e.target.value.replace(',', '.'))}
                  disabled={tipo === 'TOTAL'}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="condonar-motivo">Motivo (obligatorio)</label>
                <select id="condonar-motivo" value={motivoCodigo} onChange={(e) => setMotivoCodigo(e.target.value)} required>
                  <option value="">Selecciona un motivo…</option>
                  {MOTIVOS_CONDONACION.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="condonar-observaciones">Observaciones{motivoCodigo === 'OTRO' ? ' (obligatorias)' : ' (opcional)'}</label>
                <textarea id="condonar-observaciones" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} maxLength={500} />
              </div>

              {errores.length > 0 && (importe !== '' || motivoCodigo) && (
                <ul className="pagos-previsualizacion-error">
                  {errores.map((m) => <li key={m}>{m}</li>)}
                </ul>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={errores.length > 0}>Continuar</button>
              </div>
            </>
          ) : (
            <>
              <div className="data-notice">
                <CircleAlert size={16} strokeWidth={1.75} />
                <span>
                  Vas a condonar <strong>{formatoMoneda(importe)}</strong> ({tipoEfectivo === 'TOTAL' ? 'total' : 'parcial'}) del
                  moratorio de la cuota #{cargo.numeroPago}. El moratorio pendiente quedará en{' '}
                  <strong>{formatoMoneda(Math.max(0, pendiente - Number(importe)))}</strong>. La condonación queda registrada
                  en el historial con tu usuario y no es un cobro.
                </span>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setConfirmando(false)} disabled={enviando}>Volver</button>
                <button type="button" className="btn btn-danger" onClick={ejecutar} disabled={enviando}>
                  {enviando ? 'Condonando…' : 'Confirmar condonación'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

export default CondonarMoratorioModal;
